import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import {setLFCDataProc} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography
} from '@mui/material';

interface Props {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
  floorId: number;
  floorName: string;
  imageNo: number;
}

const FloorImageDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [floorName, setFloorName] = useState('');
  const [imageData, setImageData] = useState('');
  const [aspectRatio, setAspectRatio] = useState(1);
  const formRef = useRef<HTMLFormElement>(null!);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const onSave = () => {
    if (!formRef.current?.reportValidity()) return;

    if (props.floorId === 0 && floorName.trim() === '') {
      enqueueSnackbar(t('フロア名を入力してください'), {variant: 'error'});
      return;
    }
    if (imageData === '') {
      enqueueSnackbar(t('画像ファイルを選択してください'), {variant: 'error'});
      return;
    }

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '登録処理',
        invoke_name: 'CreateFloorImage',
        parameters: {
          floorId: props.floorId,
          floorName: props.floorId === 0 ? floorName.trim() : props.floorName,
          imageNo: props.imageNo,
          imageData,
          aspectRatio
        },
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('登録に失敗しました'), {variant: 'error'});
        })
    ]).then(() => setStartProcess(false));
  };

  const onClose = (isRefresh: boolean) => {
    props.onClose(isRefresh);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      reader.onload = e => setImageData(e.target?.result as string);
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setAspectRatio(parseFloat((img.naturalHeight / img.naturalWidth).toFixed(8)));
  };

  useEffect(() => {
    if (!props.open) {
      setFloorName('');
      setImageData('');
      setAspectRatio(1);
      return;
    }
  }, [props.open]);

  useEffect(() => {
    return () => {
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  return (
    <Dialog open={props.open} onClose={() => onClose(false)} maxWidth="xs">
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => onClose(false)}>{t('フロア画像登録')}</LFCDialogTitle>
      <DialogContent dividers>
        <form ref={formRef}>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12}>
              {props.floorId === 0 ? (
                <TextField
                  label={t('フロア名')}
                  size="small"
                  fullWidth
                  required
                  value={floorName}
                  onChange={e => setFloorName(e.target.value)}
                />
              ) : (
                <Box>
                  <Chip size="small" label={t('フロア名')} />
                  <Typography mt={0.5} ml={1}>
                    {props.floorName}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Chip size="small" label={t('画像No')} />
                <Typography mt={0.5} ml={1}>
                  {props.imageNo}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Chip size="small" label={t('画像')} />
                <Box mt={0.5} ml={1} display="flex" justifyContent="start">
                  {imageData && (
                    <img
                      src={imageData}
                      alt="Selected"
                      style={{maxWidth: '100%', maxHeight: '300px'}}
                      onLoad={handleImageLoad}
                    />
                  )}
                </Box>
                <Box mt={0.5} ml={1}>
                  <input
                    id="floor-image-file"
                    type="file"
                    accept="image/png, image/jpeg"
                    style={{display: 'none'}}
                    onChange={handleImageChange}
                  />
                  <label htmlFor="floor-image-file">
                    <Button variant="contained" component="span">
                      {t('ファイル選択')}
                    </Button>
                  </label>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <LFCButton onClick={() => onClose(false)}>{t('キャンセル')}</LFCButton>
        <LFCButton color="primary" onClick={onSave}>
          {t('登録')}
        </LFCButton>
      </DialogActions>
    </Dialog>
  );
};

export default FloorImageDialog;
